import dayjs from "@calcom/dayjs";
import { useCalendarStore } from "@calcom/features/calendars/weeklyview/state/store";
import { calculateEventLayouts, createLayoutMap } from "@calcom/features/calendars/weeklyview/utils/overlap";
import classNames from "@calcom/ui/classNames";
import { useEffect, useMemo, useState } from "react";
import { shallow } from "zustand/shallow";
import { useEventDrag } from "../../hooks/useEventDrag";
import { Event } from "./Event";

type Props = {
  day: dayjs.Dayjs;
};

export function EventList({ day }: Props) {
  const {
    startHour,
    events,
    eventOnClick,
    selectedBookingUid,
    gridCellsPerHour,
    onEventDrop,
    onEventResize,
    isEventEditable,
  } = useCalendarStore(
    (state) => ({
      startHour: state.startHour,
      events: state.events,
      eventOnClick: state.onEventClick,
      selectedBookingUid: state.selectedBookingUid,
      gridCellsPerHour: state.gridCellsPerHour,
      onEventDrop: state.onEventDrop,
      onEventResize: state.onEventResize,
      isEventEditable: state.isEventEditable,
    }),
    shallow
  );

  const { preview, beginDrag, swallowClickAfterDrag, isDragEnabled } = useEventDrag({
    gridCellsPerHour: gridCellsPerHour ?? 4,
    isEventEditable,
    onEventDrop,
    onEventResize,
  });

  const [hoveredEventId, setHoveredEventId] = useState<number | null>(null);

  const dayEvents = useMemo(() => {
    return events.filter((event) => {
      return dayjs(event.start).isSame(day, "day") && !event.options?.allDay;
    });
  }, [events, day]);

  const layoutMap = useMemo(() => {
    const layouts = calculateEventLayouts(dayEvents);
    return createLayoutMap(layouts);
  }, [dayEvents]);

  const eventCalculations = useMemo(() => {
    return new Map(
      dayEvents.map((event) => {
        const eventStart = dayjs(event.start);
        const eventEnd = dayjs(event.end);
        const eventDuration = eventEnd.diff(eventStart, "minutes");
        const eventStartHour = eventStart.hour();
        const eventStartDiff = (eventStartHour - (startHour || 0)) * 60 + eventStart.minute();

        return [
          event.id,
          {
            eventStart,
            eventDuration,
            eventStartDiff,
          },
        ];
      })
    );
  }, [dayEvents, startHour]);

  // Find which overlap group the hovered event belongs to
  const hoveredEventLayout = hoveredEventId ? layoutMap.get(hoveredEventId) : null;
  const hoveredGroupIndex = hoveredEventLayout?.groupIndex ?? null;

  // Find the event ID that matches the selected booking UID (only for events on this day)
  const selectedEventId = useMemo(() => {
    if (!selectedBookingUid) return undefined;
    const matchingEvent = dayEvents.find((event) => event.options?.bookingUid === selectedBookingUid);
    return matchingEvent?.id;
  }, [dayEvents, selectedBookingUid]);

  // Scroll to the selected event when it changes
  useEffect(() => {
    if (selectedEventId === undefined) return;

    // Use requestAnimationFrame to ensure the DOM has updated
    requestAnimationFrame(() => {
      const eventElement = document.querySelector(`[data-calendar-event-id="${selectedEventId}"]`);
      if (eventElement) {
        eventElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    });
  }, [selectedEventId]);

  return (
    <>
      {dayEvents.map((event) => {
        const layout = layoutMap.get(event.id);
        if (!layout) return null;

        const calc = eventCalculations.get(event.id);
        if (!calc) return null;

        const { eventStart, eventDuration, eventStartDiff } = calc;

        const isHovered = hoveredEventId === event.id;
        const isSelected = selectedEventId === event.id;
        const isInHoveredGroup = hoveredGroupIndex !== null && layout.groupIndex === hoveredGroupIndex;
        // Bound to a local so TypeScript keeps it non-null inside the branches below.
        const activePreview = preview?.eventId === event.id ? preview : null;
        const isDragging = activePreview !== null;
        const zIndex = isDragging ? 85 : isHovered || isSelected ? 79 : layout.baseZIndex;
        const draggable = isDragEnabled && (!isEventEditable || isEventEditable(event));

        // While dragging, the chip follows the pointer in snapped steps; the underlying event is
        // untouched until the consumer saves, so a cancelled drag needs no rollback.
        const dragOffsetY = activePreview?.mode === "move" ? activePreview.minutes : 0;
        const dragOffsetX = activePreview ? activePreview.days : 0;
        const previewDuration =
          activePreview?.mode === "resize"
            ? Math.max(1, eventDuration + activePreview.minutes)
            : eventDuration;

        return (
          <div
            key={`${event.id}-${eventStart.toISOString()}`}
            className={classNames(
              "absolute duration-100 ease-out",
              !isDragging && "transition-all",
              event.options?.borderOnly && "pointer-events-none"
            )}
            data-testid={event.options?.["data-test-id"]}
            data-calendar-event-id={event.id}
            onMouseEnter={() => setHoveredEventId(event.id)}
            onMouseLeave={() => setHoveredEventId(null)}
            onPointerDown={draggable ? (e) => beginDrag(e, event, "move") : undefined}
            onClickCapture={swallowClickAfterDrag}
            style={{
              left: `${layout.leftOffsetPercent}%`,
              width: `${layout.widthPercent}%`,
              zIndex,
              top: `calc(${eventStartDiff}*var(--one-minute-height))`,
              height: `max(15px, calc(${previewDuration}*var(--one-minute-height)))`,
              transform: activePreview
                ? `translate(${dragOffsetX * activePreview.columnWidth}px, calc(${dragOffsetY} * var(--one-minute-height)))`
                : isHovered || isSelected
                  ? "scale(1.02)"
                  : "scale(1)",
              touchAction: draggable ? "none" : undefined,
              opacity: isDragging
                ? 0.85
                : hoveredGroupIndex !== null && !isHovered && isInHoveredGroup
                  ? 0.6
                  : 1,
              cursor: draggable ? (isDragging ? "grabbing" : "grab") : undefined,
            }}>
            <Event
              event={event}
              eventDuration={previewDuration}
              onEventClick={eventOnClick}
              isHovered={isHovered}
              currentlySelectedEventId={selectedEventId}
            />
            {draggable && onEventResize && (
              <div
                // Bottom edge grip. Its own pointerdown stops the parent's move-drag from starting.
                onPointerDown={(e) => {
                  e.stopPropagation();
                  beginDrag(e, event, "resize");
                }}
                className="absolute inset-x-0 bottom-0 h-1.5 cursor-ns-resize opacity-0 hover:opacity-100"
                style={{ touchAction: "none" }}>
                <div className="bg-emphasis mx-auto h-0.5 w-6 rounded-full" />
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
